import axios from 'axios';
import { clientCache } from './clientCache';

// Enhanced API service with intelligent client-side caching

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // send cookies for auth
});

// Intercept all API responses for debugging and caching
api.interceptors.response.use(
  response => {
    console.log(`API Response [${response.config.method}] ${response.config.url}:`, 
      response.status, response.data);
    
    // Auto-cache responses for GET requests
    if (response.config.method === 'get' && response.data.success) {
      const serverTimestamp = response.data.serverTimestamp || Date.now();
      
      // Cache assets
      if (response.config.cacheAssetsResponse && response.data.data) {
        clientCache.cacheAssets(response.data.data, serverTimestamp);
      }
      
      // Cache bookings
      if (response.config.cacheBookingsResponse && response.data.data) {
        clientCache.cacheBookings(response.data.data, serverTimestamp);
      }
    }
    
    return response;
  },
  error => {
    console.error(`API Error [${error.config?.method}] ${error.config?.url}:`, 
      error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Token handling
export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

// Auth service for login/logout/me
export const authService = {
  login: async (email) => {
    const res = await api.post('/auth/login', { email });
    const { token, user } = res.data;
    setAuthToken(token);
    localStorage.setItem('token', token);
    return user;
  },
  // New registration method using magic link
  register: async (name, email, membershipNumber) => {
    const res = await api.post('/auth/register', { name, email, membershipNumber });
    return res.data;
  },
  validate: async (name, membershipNumber) => {
    const res = await api.post('/auth/validate', { name, membershipNumber });
    return res.data;
  },
  // Send magic link to existing user email
  loginRequest: async (email) => {
    const res = await api.post('/auth/login-request', { email });
    return res.data;
  },
  logout: async () => {
    await api.post('/auth/logout');
    setAuthToken(null);
    localStorage.removeItem('token');
  },
  getCurrentUser: async () => {
    const res = await api.get('/auth/me');
    return res.data.user;
  },
  me: async () => {
    const res = await api.get('/auth/me');
    return res.data;
  },
  updateEmail: async (email) => {
    const res = await api.put('/auth/update-email', { email });
    return res.data;
  }
};

// Asset services with intelligent caching
export const assetService = {
  getAll: async (forceRefresh = false) => {
    try {
      // Try cache first unless force refresh
      if (!forceRefresh) {
        const cachedAssets = await clientCache.getCachedAssets();
        if (cachedAssets) {
          console.log('📦 Using cached assets (client-side)');
          return { data: cachedAssets, fromCache: true };
        }
      }
      
      console.log('📦 Fetching assets from server...');
      const response = await api.get('/assets', {
        cacheAssetsResponse: true
      });
      
      if (!response.data.success) {
        console.error('API returned error:', response.data);
        throw new Error(response.data.error || 'Failed to fetch equipment');
      }
      
      return response.data;
    } catch (error) {
      // Fallback to cache on network error
      if (!forceRefresh) {
        const cachedAssets = await clientCache.getCachedAssets(10 * 60 * 1000); // 10 min stale cache
        if (cachedAssets) {
          console.log('📦 Using stale cached assets due to network error');
          return { data: cachedAssets, fromCache: true, stale: true };
        }
      }
      
      console.error('Error fetching assets:', error);
      throw error;
    }
  },
  
  create: async (assetData) => {
    try {
      const response = await api.post('/assets', assetData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  
  update: async (assetId, assetData) => {
    try {
      const response = await api.put(`/assets/${assetId}`, assetData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  
  delete: async (assetId) => {
    try {
      const response = await api.delete(`/assets/${assetId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  checkCompatibility: async (asset1Id, asset2Id) => {
    try {
      const response = await api.get(`/assets/${asset1Id}/compatibility/${asset2Id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Check if a set of assets are compatible with each other
  checkSetCompatibility: async (assetIds) => {
    try {
      const response = await api.post('/assets/check-compatibility', { assetIds });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

// Enhanced bookingService with intelligent caching and conflict detection
export const bookingService = {
  getAll: async (assetIds = null, forceRefresh = false, options = {}) => {
    const {
      scope = 'future',
      limit,
      beforeStart,
      beforeId
    } = options;

    try {
      // Try cache first unless force refresh
      if (!forceRefresh && scope === 'future') {
        const cachedBookings = await clientCache.getCachedBookings(assetIds);
        if (cachedBookings) {
          console.log('📅 Using cached bookings (client-side)');
          return {
            data: cachedBookings,
            meta: { scope: 'future', fromCache: true },
            fromCache: true
          };
        }
      }
      
      console.log(`📅 Fetching ${scope} bookings from server...`);
      const params = { scope };
      if (Array.isArray(assetIds) && assetIds.length > 0) {
        params.assetIds = assetIds.join(',');
      }
      if (limit) params.limit = limit;
      if (beforeStart) params.beforeStart = beforeStart;
      if (beforeId) params.beforeId = beforeId;

      const response = await api.get('/bookings', {
        params,
        cacheBookingsResponse: scope === 'future'
      });
      
      if (!response.data?.data) {
        console.error('Unexpected booking response format:', response);
        return { data: {}, meta: { scope } };
      }
      
      console.log(
        `📅 Fetched ${scope} bookings for ${Object.keys(response.data.data).length} assets`
      );
      return response.data;
    } catch (error) {
      // Fallback to cache on network error
      if (!forceRefresh && scope !== 'all' && scope !== 'past') {
        const cachedBookings = await clientCache.getCachedBookings(assetIds, 5 * 60 * 1000); // 5 min stale
        if (cachedBookings) {
          console.log('📅 Using stale cached bookings due to network error');
          return {
            data: cachedBookings,
            meta: { scope: 'future', fromCache: true, stale: true },
            fromCache: true,
            stale: true
          };
        }
      }
      
      console.error('Error in bookingService.getAll():', error);
      throw error.response?.data || error;
    }
  },

  // Fast client-side conflict checking before server validation
  checkConflictsLocally: async (assetId, startTime, endTime, excludeBookingId = null) => {
    try {
      const conflicts = await clientCache.checkConflictsLocally(assetId, startTime, endTime, excludeBookingId);
      return conflicts;
    } catch (error) {
      console.error('Error checking conflicts locally:', error);
      return [];
    }
  },
  
  getByAsset: async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/bookings`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getPastBookings: async (assetIds = [], options = {}) => {
    const { limit = 10, beforeStart = null, beforeId = null } = options;
    return bookingService.getAll(assetIds, true, {
      scope: 'past',
      limit,
      beforeStart,
      beforeId
    });
  },
  
  create: async (data) => {
    try {
      const response = await api.post('/bookings', {
        name: data.name,
        wingId: data.wingId,
        foilId: data.foilId,
        boardId: data.boardId,
        start: data.start,
        end: data.end,
        _clientTempId: data._clientTempId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  
  update: async (id, data) => {
    try {
      console.log('Updating booking, sending data:', data);
      const response = await api.put(`/bookings/${id}`, data);
      console.log('Booking update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error.response?.data || error;
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/bookings/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  
  syncAll: async () => {
    try {
      console.log('Starting sync request...');
      // Make sure this endpoint matches what's defined in your routes
      const response = await api.get('/bookings/sync'); // This should be relative
      console.log('Sync response:', response);
      return response.data;
    } catch (error) {
      console.error('Sync error:', error);
      return { success: false, data: {}, error: error.message };
    }
  }
};

// Member service for admin to manage approved sailing membership numbers
export const memberService = {
  list: async () => {
    const res = await api.get('/auth/approved-members');
    return res.data.data; // array of ApprovedMember
  },
  listPending: async () => {
    const res = await api.get('/auth/pending-users');
    return res.data.data; // array of pending users
  },
  approvePending: async (userId) => {
    const res = await api.post('/auth/approve-user', { userId });
    return res.data;
  },
  rejectPending: async (userId) => {
    const res = await api.post('/auth/reject-user', { userId });
    return res.data;
  },
  update: async (action, identifier, arg1, arg2, arg3) => {
    const payload = { action };
    
    // Support both userId (new) and sailingNumber (legacy) based operations
    if (action === 'add') {
      // Legacy admin add operation using sailing number
      payload.sailingNumber = identifier;
      payload.name = arg1;
      if (arg2) payload.email = arg2;
      if (arg3) payload.role = arg3;
    } else if (action === 'remove') {
      // Support removal by either userId or sailingNumber
      if (typeof identifier === 'string' && identifier.length === 24) {
        // MongoDB ObjectId format - use as userId
        payload.userId = identifier;
      } else {
        // Number - use as sailingNumber
        payload.sailingNumber = identifier;
      }
    } else if (action === 'update') {
      // Support update by either userId or sailingNumber
      if (typeof identifier === 'string' && identifier.length === 24) {
        // MongoDB ObjectId format - use as userId
        payload.userId = identifier;
      } else {
        // Number - use as sailingNumber
        payload.sailingNumber = identifier;
      }
      payload.field = arg1;
      payload.value = arg2;
    }
    
    const res = await api.post('/auth/approved-members', payload);
    return res.data.data || res.data;
  }
};

// default export for api instance
export default api;
